/**
 * Google Calendar OAuth Callback Page
 *
 * Google redirects here after the employee grants access.
 * The URL will contain ?code=... (success) or ?error=... (denied).
 *
 * This page:
 *  1. Reads the code from the URL
 *  2. Calls the Firebase function to exchange it for tokens
 *  3. Redirects back to /tabs/my-schedule
 */

import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonSpinner,
  IonText,
  IonButton,
  IonIcon,
  useIonRouter,
} from '@ionic/react';
import { checkmarkCircleOutline, closeCircleOutline } from 'ionicons/icons';
import { googleCalendarService } from '../services/googleCalendarService';
import styles from './GoogleCalendarCallback.module.scss';
import { useTranslation } from 'react-i18next';

type Status = 'loading' | 'success' | 'error';

// Parsed once, outside component state — the query string doesn't change
// while this page is mounted.
const oauthParams = new URLSearchParams(window.location.search);
const oauthCode = oauthParams.get('code');
const oauthError = oauthParams.get('error');

const GoogleCalendarCallback: React.FC = () => {
  const { t } = useTranslation();
  const router = useIonRouter();
  // The error/no-code case is a pure derivation of the URL's query params —
  // computed once via a lazy initializer rather than as setState calls
  // inside an effect, which now only handles the actual OAuth exchange.
  const [status, setStatus] = useState<Status>(() => (oauthError || !oauthCode ? 'error' : 'loading'));
  const [errorMessage, setErrorMessage] = useState<string>(() =>
    oauthError || !oauthCode
      ? (oauthError === 'access_denied'
        ? t('pages.GoogleCalendarCallback.youDeniedAccessToGoogleCalendar')
        : 'Google authorization failed. Please try again.')
      : ''
  );

  useEffect(() => {
    const code = oauthCode;
    if (oauthError || !code) return;

    googleCalendarService
      .handleOAuthCallback(code)
      .then(() => {
        setStatus('success');
        // Auto-redirect after 2 s — use replace so back doesn't return to Google auth
        setTimeout(() => {
          router.push('/tabs/my-schedule', 'root', 'replace');
        }, 2000);
      })
      .catch((err: unknown) => {
        console.error('Google Calendar callback error:', err);
        setErrorMessage(
          err instanceof Error
            ? err.message
            : t('pages.GoogleCalendarCallback.anUnexpectedErrorOccurredPleaseTry')
        );
        setStatus('error');
      });
  }, [router, t]);
  const goBack = () => router.push('/tabs/my-schedule', 'root', 'replace');

  return (
    <IonPage>
      <IonContent className={styles.content} fullscreen>
        <div className={styles.card}>
          {status === 'loading' && (
            <>
              <IonSpinner name="bubbles" color="primary" className={styles.spinner} />
              <IonText className={styles.message}>{t('pages.GoogleCalendarCallback.connectingYourGoogleCalendar')}
              </IonText>
            </>
          )}

          {status === 'success' && (
            <>
              <IonIcon icon={checkmarkCircleOutline} className={styles.iconSuccess} />
              <IonText className={styles.title}>{t('pages.GoogleCalendarCallback.connected')}</IonText>
              <IonText className={styles.message}>{t('pages.GoogleCalendarCallback.yourGoogleCalendarIsNowSynced')}
              </IonText>
            </>
          )}

          {status === 'error' && (
            <>
              <IonIcon icon={closeCircleOutline} className={styles.iconError} />
              <IonText className={styles.title}>{t('pages.GoogleCalendarCallback.connectionFailed')}</IonText>
              <IonText className={styles.message}>{errorMessage}</IonText>
              <IonButton onClick={goBack} className={styles.button}>{t('pages.GoogleCalendarCallback.backToSchedule')}
              </IonButton>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default GoogleCalendarCallback;
